import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Create,
  Datagrid,
  FunctionField,
  List,
  SimpleForm,
  TextField as RaTextField,
  TextInput,
  Title,
  useNotify,
  usePermissions,
  type CreateProps,
  type ListProps,
} from "react-admin";
import { Link as RouterLink, useParams } from "react-router-dom";

import { DisplayDateField } from "@/components/DisplayDateField";
import { PayrollSalarySlip } from "@/containers/staff/payroll-periods/PayrollSalarySlip";
import {
  buildPayrollLopLineExplainer,
  lopAmountFromComponents,
} from "@/containers/staff/payroll-periods/payroll-lop-explainer";
import {
  MarkPaidDialog,
  MarkUnpaidDialog,
} from "@/containers/staff/payroll-periods/PayrollPayoutDialogs";
import {
  payoutModeLabel,
  payoutSummaryText,
  type PayoutSummary,
} from "@/containers/staff/payroll-periods/payroll-payout-utils";
import { formatPayslipWageMonth } from "@/containers/staff/payroll-periods/payroll-payslip-format";
import { StaffPayrollSetupIncompleteBanner } from "@/containers/staff/shared/StaffPayrollSetupIncompleteBanner";
import {
  PAYROLL_PERIODS_CREATE,
  PAYROLL_PERIODS_EDIT,
  PAYROLL_PERIODS_PAY,
  PAYROLL_PERIODS_REVERSE_PAYOUT,
  PAYROLL_PERIODS_VIEW,
} from "@/containers/staff/shared/staff-payroll-permissions";
import { staffShowPath } from "@/containers/staff/staff/staff-show-helpers";
import { callApi } from "@/dataprovider/misc-apis";
import { useTenantBranding } from "@/hooks/useTenantBranding";
import { formatINR } from "@/utils/app-formatters";
import { hasPermission } from "@/utils/permission-utils";

type PayrollComponent = {
  id: string;
  type: string;
  label: string;
  amount: number;
};

type PayrollLine = {
  id: string;
  employeeId: number;
  employee?: { id: number; name: string };
  employeeName?: string | null;
  monthlyGross: number;
  lopDays: number;
  workingDaysInMonth?: number | null;
  presentDays?: number | null;
  absentDays?: number | null;
  leaveDays?: number | null;
  holidayDays?: number | null;
  leaveAvailableDays?: number | null;
  earningsAmount: number;
  deductionsAmount: number;
  netPay: number;
  professionalTaxAmount: number;
  payoutStatus?: "PENDING" | "PAID";
  payoutPaidOn?: string | null;
  payoutMode?: string | null;
  payoutReference?: string | null;
  payoutReversalNote?: string | null;
  components?: PayrollComponent[];
};

type PayrollPeriod = {
  id: string;
  yearMonth: string;
  status: "DRAFT" | "FINALIZED";
  payrollDayBasis: string;
  professionalTaxProfile: string;
  payoutSummary?: PayoutSummary;
  lines?: PayrollLine[];
};

function PayrollLopInfoIcon({
  period,
  line,
  lopDaysOverride,
}: {
  period: Pick<PayrollPeriod, "yearMonth" | "payrollDayBasis">;
  line: PayrollLine;
  /** Draft input value when editing LOP days before save. */
  lopDaysOverride?: number;
}) {
  const lopDays =
    lopDaysOverride != null && Number.isFinite(lopDaysOverride) ? lopDaysOverride : line.lopDays;
  const explainer = buildPayrollLopLineExplainer({
    yearMonth: period.yearMonth,
    payrollDayBasis: period.payrollDayBasis,
    monthlyGross: line.monthlyGross,
    lopDays,
    workingDaysInMonth: line.workingDaysInMonth,
    // When previewing draft LOP days, recompute; otherwise use saved component amount.
    lopAmount: lopDaysOverride != null ? null : lopAmountFromComponents(line.components),
    presentDays: line.presentDays,
    absentDays: line.absentDays,
    leaveDays: line.leaveDays,
    holidayDays: line.holidayDays,
    leaveAvailableDays: line.leaveAvailableDays,
  });

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 320, py: 0.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {explainer.title}
          </Typography>
          {explainer.bodyLines.map((text) => (
            <Typography
              key={text}
              variant="caption"
              component="div"
              sx={{ display: "block", mb: 0.5 }}
            >
              {text}
            </Typography>
          ))}
        </Box>
      }
    >
      <IconButton
        size="small"
        aria-label={explainer.title}
        sx={{ p: 0.25, color: "text.secondary" }}
      >
        <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
      </IconButton>
    </Tooltip>
  );
}

export function PayrollPeriodsList(props: ListProps) {
  return (
    <List {...props} sort={{ field: "yearMonth", order: "DESC" }} perPage={25}>
      <StaffPayrollSetupIncompleteBanner />
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <RaTextField source="yearMonth" label="Month" />
        <RaTextField source="status" label="Status" />
        <RaTextField source="payrollDayBasis" label="Day basis" />
        <RaTextField source="professionalTaxProfile" label="PT profile" />
        <FunctionField
          label="Payout"
          render={(record: PayrollPeriod) =>
            record.status === "FINALIZED" && record.payoutSummary
              ? payoutSummaryText(record.payoutSummary, formatINR)
              : "—"
          }
        />
        <DisplayDateField source="createdAt" label="Created" showTime />
      </Datagrid>
    </List>
  );
}

export function PayrollPeriodsCreate(props: CreateProps) {
  return (
    <Create {...props} redirect="show">
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 420 }}>
        <StaffPayrollSetupIncompleteBanner dense />
        <TextInput
          source="yearMonth"
          label="Wage month (YYYY-MM)"
          isRequired
          fullWidth
          helperText="Creates a DRAFT payroll run for the calendar month."
        />
      </SimpleForm>
    </Create>
  );
}

export function PayrollPeriodShowPage() {
  const { id } = useParams();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, PAYROLL_PERIODS_VIEW);
  const canCreate = hasPermission(permissions, PAYROLL_PERIODS_CREATE);
  const canEdit = hasPermission(permissions, PAYROLL_PERIODS_EDIT);
  const canPay = hasPermission(permissions, PAYROLL_PERIODS_PAY);
  const canReverse = hasPermission(permissions, PAYROLL_PERIODS_REVERSE_PAYOUT);

  const [payLine, setPayLine] = useState<PayrollLine | null>(null);
  const [payAllOpen, setPayAllOpen] = useState(false);
  const [unpayLine, setUnpayLine] = useState<PayrollLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [lopDrafts, setLopDrafts] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    if (!id || !canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    callApi({ location: `payrollPeriods/${id}`, method: "GET" })
      .then((response) => {
        const body = response.data as PayrollPeriod | { rows?: PayrollPeriod[] };
        const data = (
          body && typeof body === "object" && "rows" in body && Array.isArray(body.rows)
            ? body.rows[0]
            : body
        ) as PayrollPeriod;
        setPeriod(data);
        const next: Record<string, string> = {};
        for (const line of data.lines ?? []) {
          next[line.id] = String(line.lopDays ?? 0);
        }
        setLopDrafts(next);
      })
      .catch(() => notify("Failed to load payroll period", { type: "error" }))
      .finally(() => setLoading(false));
  }, [canView, id, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      notify(success, { type: "success" });
      load();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const apiMessage = err?.response?.data?.message;
      notify(
        Array.isArray(apiMessage)
          ? apiMessage.join(", ")
          : apiMessage || err?.message || "Payroll action failed",
        { type: "error" }
      );
    } finally {
      setBusy(false);
    }
  };

  if (!canView) return null;
  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!period) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Payroll period not found.</Alert>
      </Box>
    );
  }

  const isDraft = period.status === "DRAFT";
  const summary = period.payoutSummary;
  const lineName = (l: PayrollLine) =>
    l.employee?.name ?? l.employeeName ?? `Employee #${l.employeeId}`;
  const onPayoutDone = (message: string) => {
    setPayLine(null);
    setPayAllOpen(false);
    setUnpayLine(null);
    notify(message, { type: "success" });
    load();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title={`Payroll ${period.yearMonth}`} />
      <StaffPayrollSetupIncompleteBanner />
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Payroll {period.yearMonth}</Typography>
            <Typography variant="body2" color="text.secondary">
              Status {period.status} · {period.payrollDayBasis} · PT {period.professionalTaxProfile}
            </Typography>
          </Box>
          <Button component={RouterLink} to="/payrollPeriods" size="small">
            Back to list
          </Button>
          {isDraft && canCreate ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() =>
                runAction(
                  () =>
                    callApi({
                      location: `payrollPeriods/${period.id}/generate`,
                      method: "POST",
                    }),
                  "Payroll draft generated"
                )
              }
            >
              Generate / refresh
            </Button>
          ) : null}
          {isDraft && canEdit ? (
            <Button
              variant="contained"
              disabled={busy || !(period.lines && period.lines.length > 0)}
              onClick={() =>
                runAction(
                  () =>
                    callApi({
                      location: `payrollPeriods/${period.id}/finalize`,
                      method: "POST",
                    }),
                  "Payroll finalized"
                )
              }
            >
              Finalize
            </Button>
          ) : null}
        </Stack>

        {!isDraft && summary ? (
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">
              <strong>Payout:</strong> {payoutSummaryText(summary, formatINR)}
            </Typography>
            {canPay && summary.pendingCount > 0 ? (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => setPayAllOpen(true)}
              >
                Mark all pending as paid
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {!period.lines?.length ? (
          <Alert severity="info">
            No lines yet. Click Generate to build draft payslips for salaried staff.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Present</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">Leave</TableCell>
                <TableCell align="right">Holiday</TableCell>
                <TableCell align="right">Leave avail.</TableCell>
                <TableCell align="right">LOP days</TableCell>
                <TableCell align="right">Earnings</TableCell>
                <TableCell align="right">Deductions</TableCell>
                <TableCell align="right">Net</TableCell>
                {!isDraft ? <TableCell>Payout</TableCell> : null}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {period.lines.map((line) => {
                const draftLop = Number(lopDrafts[line.id] ?? line.lopDays);
                const lopAmount = lopAmountFromComponents(line.components);
                return (
                  <TableRow key={line.id}>
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={staffShowPath(line.employeeId, period.yearMonth)}
                        underline="hover"
                      >
                        {line.employee?.name ?? line.employeeName ?? `Employee #${line.employeeId}`}
                      </MuiLink>
                    </TableCell>
                    <TableCell align="right">{formatINR(line.monthlyGross)}</TableCell>
                    <TableCell align="right">{line.presentDays ?? "—"}</TableCell>
                    <TableCell align="right">{line.absentDays ?? "—"}</TableCell>
                    <TableCell align="right">{line.leaveDays ?? "—"}</TableCell>
                    <TableCell align="right">{line.holidayDays ?? "—"}</TableCell>
                    <TableCell align="right">{line.leaveAvailableDays ?? "—"}</TableCell>
                    <TableCell align="right">
                      {isDraft && canEdit ? (
                        <TextField
                          size="small"
                          type="number"
                          value={lopDrafts[line.id] ?? "0"}
                          onChange={(e) =>
                            setLopDrafts((prev) => ({ ...prev, [line.id]: e.target.value }))
                          }
                          inputProps={{ min: 0, step: 0.5 }}
                          sx={{ width: 88 }}
                        />
                      ) : (
                        line.lopDays
                      )}
                    </TableCell>
                    <TableCell align="right">{formatINR(line.earningsAmount)}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <Typography variant="body2" component="span">
                          {formatINR(line.deductionsAmount)}
                        </Typography>
                        <PayrollLopInfoIcon
                          period={period}
                          line={line}
                          lopDaysOverride={Number.isFinite(draftLop) ? draftLop : line.lopDays}
                        />
                      </Stack>
                      {lopAmount != null && lopAmount > 0 ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          incl. LOP {formatINR(lopAmount)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{formatINR(line.netPay)}</TableCell>
                    {!isDraft ? (
                      <TableCell>
                        {line.payoutStatus === "PAID" ? (
                          <>
                            <Chip size="small" color="success" label="Paid" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {line.payoutPaidOn} · {payoutModeLabel(line.payoutMode)}
                              {line.payoutReference ? ` · ${line.payoutReference}` : ""}
                            </Typography>
                          </>
                        ) : line.netPay <= 0 ? (
                          <Chip size="small" label="Nothing to pay" />
                        ) : (
                          <>
                            <Chip size="small" color="warning" label="Pending" />
                            {line.payoutReversalNote ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                Reversed: {line.payoutReversalNote}
                              </Typography>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                    ) : null}
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {!isDraft && canPay && line.payoutStatus !== "PAID" && line.netPay > 0 ? (
                          <Button size="small" disabled={busy} onClick={() => setPayLine(line)}>
                            Mark paid
                          </Button>
                        ) : null}
                        {!isDraft && canReverse && line.payoutStatus === "PAID" ? (
                          <Button
                            size="small"
                            color="error"
                            disabled={busy}
                            onClick={() => setUnpayLine(line)}
                          >
                            Reverse
                          </Button>
                        ) : null}
                        {isDraft && canEdit ? (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              runAction(
                                () =>
                                  callApi({
                                    location: `payrollPeriods/${period.id}/lines/${line.id}`,
                                    method: "PATCH",
                                    data: { lopDays: Number(lopDrafts[line.id] ?? 0) },
                                  }),
                                "Line updated"
                              )
                            }
                          >
                            Save LOP
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          component={RouterLink}
                          to={`/payrollPeriods/${period.id}/payslip/${line.id}`}
                        >
                          Payslip
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Stack>
      {payLine ? (
        <MarkPaidDialog
          open
          kind="line"
          periodId={period.id}
          yearMonth={period.yearMonth}
          lineId={payLine.id}
          employeeName={lineName(payLine)}
          amount={payLine.netPay}
          onClose={() => setPayLine(null)}
          onDone={onPayoutDone}
        />
      ) : null}
      {payAllOpen && summary ? (
        <MarkPaidDialog
          open
          kind="all"
          periodId={period.id}
          yearMonth={period.yearMonth}
          count={summary.pendingCount}
          amount={summary.pendingAmount}
          onClose={() => setPayAllOpen(false)}
          onDone={onPayoutDone}
        />
      ) : null}
      {unpayLine ? (
        <MarkUnpaidDialog
          open
          periodId={period.id}
          lineId={unpayLine.id}
          employeeName={lineName(unpayLine)}
          onClose={() => setUnpayLine(null)}
          onDone={onPayoutDone}
        />
      ) : null}
    </Box>
  );
}

export function PayrollPayslipPage() {
  const { id, lineId } = useParams();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const { tenantName, trustName, logoUrl } = useTenantBranding();
  const canView = hasPermission(permissions, PAYROLL_PERIODS_VIEW);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [line, setLine] = useState<PayrollLine | null>(null);

  useEffect(() => {
    if (!id || !lineId || !canView) {
      setLoading(false);
      return;
    }
    callApi({ location: `payrollPeriods/${id}`, method: "GET" })
      .then((response) => {
        const body = response.data as PayrollPeriod | { rows?: PayrollPeriod[] };
        const data = (
          body && typeof body === "object" && "rows" in body && Array.isArray(body.rows)
            ? body.rows[0]
            : body
        ) as PayrollPeriod;
        setPeriod(data);
        setLine(data.lines?.find((l) => l.id === lineId) ?? null);
      })
      .catch(() => notify("Failed to load payslip", { type: "error" }))
      .finally(() => setLoading(false));
  }, [canView, id, lineId, notify]);

  if (!canView) return null;
  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!period || !line) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Payslip not found.</Alert>
      </Box>
    );
  }

  const wageMonthLabel = formatPayslipWageMonth(period.yearMonth);

  return (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      <Title title={`Salary slip ${wageMonthLabel}`} />
      <Stack direction="row" spacing={1} sx={{ mb: 2, "@media print": { display: "none" } }}>
        <Button component={RouterLink} to={`/payrollPeriods/${period.id}/show`} size="small">
          Back
        </Button>
        <Button variant="contained" size="small" onClick={() => window.print()}>
          Print
        </Button>
      </Stack>
      {line.payoutStatus === "PAID" ? (
        <Alert severity="success" sx={{ mb: 2, "@media print": { display: "none" } }}>
          Paid on {line.payoutPaidOn} via {payoutModeLabel(line.payoutMode)}
          {line.payoutReference ? ` (${line.payoutReference})` : ""}.
        </Alert>
      ) : null}
      <PayrollSalarySlip
        tenantName={tenantName}
        trustName={trustName}
        logoUrl={logoUrl}
        period={period}
        line={line}
      />
    </Box>
  );
}

export default {
  list: PayrollPeriodsList,
  create: PayrollPeriodsCreate,
  show: PayrollPeriodShowPage,
  payslip: PayrollPayslipPage,
  icon: PaymentsIcon,
  label: "Payroll",
  options: { label: "Payroll" },
  name: "payrollPeriods",
};
